import { useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { DollarSign, Clock, CheckCircle2, FileDown, FileSpreadsheet, Search, RefreshCcw, Eye, Stethoscope } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDMY } from "@/lib/dateFormat";
import { todayISO, currentMonthISO } from "@/lib/dateFormat";

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const startOfMonth = () => `${currentMonthISO()}-01`;
const today = () => todayISO();

const Kpi = ({ icon: Icon, label, value, accent }) => (
  <Card className="p-5 flex items-start gap-4">
    <div className={`h-10 w-10 rounded-md flex items-center justify-center shrink-0 ${accent}`}>
      <Icon className="h-5 w-5 text-white" />
    </div>
    <div>
      <div className="text-xs uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  </Card>
);

export default function InvoicesReport() {
  const [sedes, setSedes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], totals: {}, by_sede: [], by_modality: {} });

  const [diag, setDiag] = useState(null);
  const [filters, setFilters] = useState({
    from: startOfMonth(),
    to: today(),
    sede_id: "all",
    modality: "all",
    status: "all",
    search: "",
  });

  useEffect(() => {
    api.get("/sedes").then((r) => setSedes(r.data || [])).catch(() => {});
  }, []);

  const buildParams = () => {
    const p = { from: filters.from, to: filters.to };
    if (filters.sede_id && filters.sede_id !== "all") p.sede_id = filters.sede_id;
    if (filters.modality && filters.modality !== "all") p.modality = filters.modality;
    if (filters.status && filters.status !== "all") p.status = filters.status;
    if (filters.search.trim()) p.search = filters.search.trim();
    return p;
  };

  const load = () => {
    setLoading(true);
    api
      .get("/reports/invoices", { params: buildParams() })
      .then((r) => setData(r.data || { items: [], totals: {} }))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = data.totals || {};

  const viewInvoice = async (enrollmentId, paymentId) => {
    // Opens the invoice PDF inline in a new browser tab (no auto-download).
    try {
      const r = await api.get(`/enrollments/${enrollmentId}/invoice`, {
        params: { lang: (localStorage.getItem("lang") || "es"), payment_id: paymentId },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo abrir la factura");
    }
  };

  const download = async (format) => {
    const params = new URLSearchParams(buildParams()).toString();
    const url = `${API_BASE}/api/reports/invoices.${format}?${params}`;
    const res = await fetch(url, {
      credentials: "include",
      headers: api.defaults.headers.common["Authorization"]
        ? { Authorization: api.defaults.headers.common["Authorization"] }
        : {},
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    if (format === "pdf") {
      // Open the PDF inline in a new tab so the user can preview before
      // deciding to download.
      window.open(objUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
      return;
    }
    // CSV / other formats → explicit download since browsers cannot render CSV
    const cd = res.headers.get("Content-Disposition") || "";
    const m = cd.match(/filename="([^"]+)"/);
    const fname = m ? m[1] : `reporte_facturas.${format}`;
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  };

  const rows = data.items || [];

  return (
    <div className="space-y-6" data-testid="invoices-report">
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          data-testid="inv-diagnostics-button"
          onClick={async () => {
            try {
              const r = await api.get("/invoices/diagnostics");
              setDiag(r.data);
            } catch (e) {
              toast.error("No se pudo generar el diagnóstico");
            }
          }}
        >
          <Stethoscope className="h-4 w-4" /> Diagnóstico de numeración
        </Button>
        <Button
          variant="outline"
          size="sm"
          data-testid="inv-backfill-button"
          onClick={async () => {
            let n = 0;
            try {
              const pre = await api.post("/invoices/backfill?dry_run=true");
              n = pre.data.would_issue;
            } catch (e) {
              toast.error("No se pudo verificar");
              return;
            }
            if (n === 0) { toast.success("Todos los cobros ya tienen su consecutivo"); return; }
            if (!window.confirm(`Se emitirán ${n} factura(s) con su consecutivo para los cobros que aún no tienen número. ¿Continuar?`)) return;
            try {
              const r = await api.post("/invoices/backfill");
              const f = r.data.failed || [];
              if (f.length) {
                toast.error(`Emitidas: ${r.data.issued}. No se pudo con ${f.length}: ${f[0].student || f[0].payment_id} (ciclo ${f[0].cycle}) — ${f[0].reason}`, { duration: 12000 });
              } else {
                toast.success(`Facturas emitidas: ${r.data.issued}`);
              }
              load();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "No se pudo completar");
            }
          }}
        >
          Emitir consecutivos faltantes
        </Button>
      </div>
      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Desde</label>
            <Input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters({ ...filters, from: e.target.value })}
              data-testid="inv-filter-from"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Hasta</label>
            <Input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters({ ...filters, to: e.target.value })}
              data-testid="inv-filter-to"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Sede</label>
            <Select
              value={filters.sede_id}
              onValueChange={(v) => setFilters({ ...filters, sede_id: v })}
            >
              <SelectTrigger data-testid="inv-filter-sede"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {sedes.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Modalidad</label>
            <Select
              value={filters.modality}
              onValueChange={(v) => setFilters({ ...filters, modality: v })}
            >
              <SelectTrigger data-testid="inv-filter-modality"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="presencial">Presencial</SelectItem>
                <SelectItem value="online">Online</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Estado</label>
            <Select
              value={filters.status}
              onValueChange={(v) => setFilters({ ...filters, status: v })}
            >
              <SelectTrigger data-testid="inv-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="paid">Pagadas</SelectItem>
                <SelectItem value="pending">Pendientes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Buscar</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Estudiante o programa"
                className="pl-8"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && load()}
                data-testid="inv-filter-search"
              />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <Button onClick={load} disabled={loading} data-testid="inv-apply-filters">
            <RefreshCcw className="h-4 w-4 mr-2" />
            {loading ? "Cargando…" : "Aplicar filtros"}
          </Button>
          <div className="grow" />
          <Button variant="outline" onClick={() => download("csv")} data-testid="inv-export-csv">
            <FileSpreadsheet className="h-4 w-4 mr-2" /> Exportar CSV
          </Button>
          <Button variant="outline" onClick={() => download("pdf")} data-testid="inv-export-pdf">
            <FileDown className="h-4 w-4 mr-2" /> Exportar PDF
          </Button>
        </div>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={CheckCircle2} label="Total pagado" value={fmt(totals.paid_total)} accent="bg-green-600" />
        <Kpi icon={Clock} label="Por cobrar" value={fmt(totals.pending_total)} accent="bg-orange-500" />
        <Kpi icon={DollarSign} label="Facturas pagadas" value={totals.paid_count ?? 0} accent="bg-emerald-600" />
        <Kpi icon={DollarSign} label="Facturas pendientes" value={totals.pending_count ?? 0} accent="bg-amber-600" />
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>N° Factura</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead>Estudiante</TableHead>
              <TableHead>Programa</TableHead>
              <TableHead className="text-right">Monto</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Forma de pago</TableHead>
              <TableHead className="text-right">Factura</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-slate-500 py-10">
                  {loading ? "Cargando…" : "No hay facturas para estos filtros."}
                </TableCell>
              </TableRow>
            )}
            {rows.map((it) => (
              <TableRow key={it.id} data-testid={`inv-row-${it.id}`}>
                <TableCell className="font-mono text-xs font-semibold text-slate-900">
                  {it.invoice_number ? (
                    <span className={it.invoice_status === "annulled" ? "line-through text-slate-400" : ""}>
                      {it.invoice_number}
                      {it.invoice_status === "annulled" && (
                        <span className="ml-1 no-underline text-[10px] uppercase tracking-wider text-red-500">anulada</span>
                      )}
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs text-slate-600">
                  {formatDMY((it.paid_date || it.created_at || "").slice(0, 10))}
                </TableCell>
                <TableCell>
                  <div className="font-medium text-slate-900">{it.student_name}</div>
                  <div className="text-xs text-slate-500">{it.student_email}</div>
                </TableCell>
                <TableCell>
                  <div>{it.program_name}</div>
                  {it.group_name && <div className="text-xs text-slate-500">{it.group_name}</div>}
                </TableCell>
                <TableCell className="text-right font-mono font-semibold">
                  {fmt(it.amount)} <span className="text-xs text-slate-400">{it.currency}</span>
                </TableCell>
                <TableCell>
                  {it.status === "paid" ? (
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-100 border-0">Pagada</Badge>
                  ) : it.status === "pending" ? (
                    <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100 border-0">Pendiente</Badge>
                  ) : (
                    <Badge variant="secondary">{it.status}</Badge>
                  )}
                </TableCell>
                <TableCell className="text-xs text-slate-600">
                  {it.payment_method === "cash" ? "Efectivo"
                    : it.payment_method === "deposit" ? "Depósito"
                    : it.payment_method === "card" ? "Tarjeta"
                    : it.payment_method === "unspecified" ? <span className="text-slate-400 italic">Sin especificar</span>
                    : <span className="text-slate-300">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  {it.enrollment_id ? (
                    <button
                      type="button"
                      onClick={() => viewInvoice(it.enrollment_id, it.id)}
                      className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-slate-100 text-slate-600"
                      title="Ver factura PDF"
                      data-testid={`view-invoice-${it.id}`}
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </button>
                  ) : <span className="text-slate-300">—</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* By sede + modality breakdown */}
      {(data.by_sede?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="p-5">
            <h4 className="font-display text-lg font-semibold mb-3">Por sede</h4>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sede</TableHead>
                  <TableHead className="text-right">Pagado</TableHead>
                  <TableHead className="text-right">Pendiente</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.by_sede.map((r) => (
                  <TableRow key={r.sede_id}>
                    <TableCell>{r.sede_name}</TableCell>
                    <TableCell className="text-right font-mono text-green-700">{fmt(r.paid)}</TableCell>
                    <TableCell className="text-right font-mono text-orange-600">{fmt(r.pending)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className="p-5">
            <h4 className="font-display text-lg font-semibold mb-3">Por modalidad (pagado)</h4>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Modalidad</TableHead>
                  <TableHead className="text-right">Total pagado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell>Presencial</TableCell>
                  <TableCell className="text-right font-mono">{fmt(data.by_modality?.presencial)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Online</TableCell>
                  <TableCell className="text-right font-mono">{fmt(data.by_modality?.online)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        </div>
      )}

      {/* Por forma de pago */}
      {data.by_method && (
        <Card className="p-5">
          <h4 className="font-display text-lg font-semibold mb-3">Ingresos por forma de pago (solo pagados)</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: "cash", label: "Efectivo", color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
              { key: "deposit", label: "Depósito", color: "text-blue-700 bg-blue-50 border-blue-200" },
              { key: "card", label: "Tarjeta", color: "text-violet-700 bg-violet-50 border-violet-200" },
              { key: "unspecified", label: "Sin especificar", color: "text-slate-600 bg-slate-50 border-slate-200" },
            ].map((m) => (
              <div key={m.key} className={`rounded-lg border p-4 ${m.color}`} data-testid={`method-total-${m.key}`}>
                <div className="text-xs uppercase tracking-wider font-medium mb-1 opacity-70">{m.label}</div>
                <div className="font-display text-2xl font-bold">{fmt(data.by_method[m.key] || 0)}</div>
                <div className="text-[10px] opacity-60 mt-0.5">USD</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Dialog open={!!diag} onOpenChange={(o) => !o && setDiag(null)}>
        <DialogContent className="max-w-3xl" data-testid="inv-diagnostics-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Diagnóstico de numeración</DialogTitle>
          </DialogHeader>
          {diag && (
            <div className="space-y-5 max-h-[70vh] overflow-y-auto text-sm">
              <p className="text-slate-500 text-xs">
                {diag.charges} cobros revisados. Este reporte no modifica nada.
              </p>
              <div className={`text-xs rounded-md px-3 py-2 border ${String(diag.email_base_url).includes("preview") ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}
                   data-testid="diag-email-base-url">
                Los botones de los correos apuntan a: <strong>{diag.email_base_url}</strong>
                {String(diag.email_base_url).includes("preview") && " — ¡ojo! los estudiantes recibirían enlaces al preview"}
              </div>
              {[
                { key: "missing_number", title: "Cobros sin número de factura", tone: "text-red-700",
                  hint: "Se arreglan con el botón «Emitir consecutivos faltantes»." },
                { key: "misassigned", title: "Número en el ciclo equivocado", tone: "text-amber-700",
                  hint: "Este cobro tiene número pero un ciclo anterior de la misma matrícula no." },
                { key: "closed_without_sent_flag", title: "Ciclos completos sin el sello «factura enviada»", tone: "text-slate-700",
                  hint: "Llegaron al total de clases pero el cierre nunca se ejecutó." },
              ].map((s) => (
                <div key={s.key} data-testid={`diag-${s.key}`}>
                  <div className={`font-semibold ${s.tone}`}>
                    {s.title} · {(diag[s.key] || []).length}
                  </div>
                  <div className="text-[11px] text-slate-500 mb-1">{s.hint}</div>
                  {(diag[s.key] || []).length === 0 ? (
                    <div className="text-xs text-emerald-700">Sin casos</div>
                  ) : (
                    <div className="border border-slate-100 rounded-md divide-y divide-slate-50">
                      {(diag[s.key] || []).map((r) => (
                        <div key={r.payment_id + s.key} className="flex items-center gap-3 px-3 py-1.5 text-xs">
                          <span className="flex-1 truncate">{r.student}</span>
                          <span className="w-16">ciclo {r.cycle}</span>
                          <span className="w-24">{r.consumed}/{r.included}</span>
                          <span className="w-20">{r.status}</span>
                          <span className="w-24 text-right font-semibold">{r.invoice || "—"}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div data-testid="diag-duplicated_numbers">
                <div className="font-semibold text-red-700">
                  Números usados por más de un cobro · {(diag.duplicated_numbers || []).length}
                </div>
                {(diag.duplicated_numbers || []).length === 0
                  ? <div className="text-xs text-emerald-700">Sin casos</div>
                  : (diag.duplicated_numbers || []).map((d) => (
                      <div key={d.invoice} className="text-xs px-3 py-1">{d.invoice} → {d.payments.join(", ")}</div>
                    ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
